import type { IMessage } from '@stomp/stompjs'

import { appState } from '../appstate.svelte'
import { webSocket } from '$lib/websocket.svelte'
import { getLotInformation, getPopInformation } from '$mapapp/data/data.svelte'
import { LOT_PROVIDES_MATERIAL, LotType } from '$types/enums'
import type { MapMarker } from '$mapapp/MapApp/MapView/Marker/BasicMarker.model'
import { miniLotToLot, miniLotToMatchRequest, type MiniLot } from '$mapapp/types/miniLot'
import type {
	GenericRequestMatchMarkerMessage,
	LocationType,
	LotMatchPartner,
	MatchCoordinates,
	MatchingOverview,
	MiniLotMaterialProperties,
	MiniLotRequestMatchMarkerMessage,
	PopMatchPartner
} from '../../data/data.model'
import { toMarkerId } from './model'
import type { AutoSelection } from './selection'
import type { Matching } from '../matching.svelte'

/**
 * open the session's subscriptions and issue its first request.
 *
 * HINT: subscribe once per session here, not inside the fetch. each `addSubscription` registers a
 * fresh STOMP callback and the websocket layer does not deduplicate by destination, so subscribing
 * per fetch would pile up callbacks and fire them all on every message.
 *
 * both minilot subscriptions MUST be registered before the fetch, not after. `addSubscription` sends
 * a STOMP SUBSCRIBE frame and `startStreamRequest` a request SEND frame on the same ordered wire, in
 * call order. the `/matchMarker` request is what makes the server publish the one-shot
 * materialProperties frame and its terminal endMessage, so fetching first races the SUBSCRIBE to the
 * broker and the frame can be published before the subscription exists, then silently dropped.
 */
export function openMatchStream(session: Matching, autoSelect: AutoSelection): void {
	subscribeMatchFrames(session, autoSelect)
	if (session.miniLot) {
		subscribeMiniLotCoordinates(session)
		subscribeMiniLotProperties(session)
		requestMatchesForMiniLot(session)
	} else {
		requestMatches(session)
	}
}

function subscribeMatchFrames(session: Matching, autoSelect: AutoSelection): void {
	webSocket.addSubscription('/return/matches', (message: IMessage) => {
		try {
			// matchReferenceData is resolved separately via fetchReference()
			const data: MatchingOverview = JSON.parse(message.body)
			if (data.requestId !== session.requestId) return

			for (const match of data.matchOverviews) {
				session.matches.set(toMarkerId(match.partner.objType, match.partner.objId), match)
			}
			webSocket.onStreamMessage('/return/matches', data.requestId, data.endMessage)
			autoSelect.noteRankingData()
			autoSelect.apply()
		} catch (error) {
			console.error(error)
			throw new Error('Could not parse message body: ', { cause: error })
		}
	})
}

export function subscribeMiniLotCoordinates(session: Matching): void {
	webSocket.addSubscription('/return/minilot/coordinates', (message: IMessage) => {
		try {
			const data: MatchCoordinates = JSON.parse(message.body)
			if (data.requestId !== session.requestId) return

			/**
			 * the layer colours and filters the reference like any other marker, so it needs the
			 * reference's own type. it stays undefined while the reference has not resolved, which is
			 * possible here because this frame and `fetchReference` race.
			 */
			const reference = session.matchReferenceData
			let filterType: (LotType | LocationType)[] | undefined
			if (reference?.objType === 'lot') {
				filterType = [(reference as LotMatchPartner).type]
			} else if (reference?.objType === 'pop') {
				filterType = (reference as PopMatchPartner).types
			}

			session.referenceMarker = {
				markerId: 'minilot',
				lat: data.lat,
				lon: data.lon,
				type: 'lot',
				filterType
			} as MapMarker
			appState.selectedMarker = session.referenceMarker
			webSocket.onStreamMessage(
				'/return/minilot/coordinates',
				`${data.requestId}:minilot`,
				data.endMessage
			)
		} catch (error) {
			console.error(error)
			throw new Error('Could not parse message body', { cause: error })
		}
	})
}

export function subscribeMiniLotProperties(session: Matching): void {
	webSocket.addSubscription('/return/minilot/materialProperties', (message: IMessage) => {
		try {
			const data: MiniLotMaterialProperties = JSON.parse(message.body)
			if (data.requestId !== session.requestId) {
				return
			}

			/**
			 * in the minilot case the lot is derived both from matchReferenceData (in
			 * MatchingResults.svelte, as the prop for ReferenceEntity.svelte) and from the miniLot (in
			 * fetchReference, which runs in an `$effect`). since those can happen concurrently, the
			 * recyclingClasses are set on both. matchReferenceData may not be resolved yet when this
			 * message arrives, or may be a pop, so the access is guarded.
			 */
			if (session.matchReferenceData?.objType === 'lot') {
				const lotReference = session.matchReferenceData as LotMatchPartner
				lotReference.recyclingClasses = data.recyclingClasses
			}
			if (session.miniLot) {
				session.miniLot.recyclingClasses = data.recyclingClasses
				session.miniLot.materialProperties = data.materialProperties
			}

			webSocket.onStreamMessage(
				'/return/minilot/materialProperties',
				`${data.requestId}:minilot:materialProperties`,
				data.endMessage
			)
		} catch (error) {
			console.error(error)
			throw new Error('Could not parse message body', { cause: error })
		}
	})
}

export function requestMatches(session: Matching): void {
	session.matches.clear()
	const payload: GenericRequestMatchMarkerMessage = {
		lat: session.referenceMarker?.lat,
		lon: session.referenceMarker?.lon,
		referenceId: session.referenceId!,
		referenceType: session.selectedMarkerType!,
		profileId: session.profileId,
		radiusFrom: session.radius.from,
		radiusTo: session.radius.to
	}
	session.requestId = webSocket.startStreamRequest('/return/matches', '/matchMarker', payload)
}

export function requestMatchesForMiniLot(session: Matching): void {
	session.matches.clear()
	const payload: MiniLotRequestMatchMarkerMessage = {
		miniLot: miniLotToMatchRequest(session.miniLot!),
		referenceType: 'miniLot',
		radiusFrom: session.radius.from,
		radiusTo: session.radius.to
	}
	session.requestId = webSocket.startStreamRequest('/return/matches', '/matchMarker', payload)
	/**
	 * the minilot streams use a derived id so they do not overwrite '/return/matches' in
	 * WebSock.activeRequests, which holds one stream per requestId.
	 */
	webSocket.markRequestStart('/return/minilot/coordinates', `${session.requestId}:minilot`)
	webSocket.markRequestStart(
		'/return/minilot/materialProperties',
		`${session.requestId}:minilot:materialProperties`
	)
}

/**
 * abort the in-flight matching run on the server. safe to call repeatedly, because a stale or
 * unknown requestId is a no-op on the backend, and the local registry entry is dropped so
 * {@link Matching.matchesLoading} flips immediately. the `:minilot` suffix is a local-only derived
 * id (see {@link requestMatchesForMiniLot}), so it is released locally without a server call.
 */
export function closeMatchStream(session: Matching): void {
	if (!session.requestId) return

	webSocket.stopStreamRequest('/stopMatchMarker', session.requestId)
	webSocket.markRequestEnd(`${session.requestId}:minilot`)
	webSocket.markRequestEnd(`${session.requestId}:minilot:materialProperties`)
}

/**
 * resolves the reference of a session — the lot, pop or miniLot being matched from — shaped as the
 * partner type the matching views expect.
 *
 * the miniLot is a parameter rather than a read off the session, which keeps this callable without
 * one.
 */
export async function fetchReference(
	id: string | undefined,
	type: 'pop' | 'lot' | 'miniLot',
	miniLot: MiniLot | undefined
): Promise<LotMatchPartner | PopMatchPartner | undefined> {
	if (type === 'miniLot' && miniLot) {
		// full lot shape so LotHeader has volume/material/address; see docs/MINILOT.md
		return {
			...miniLotToLot(miniLot),
			providesMaterial: LOT_PROVIDES_MATERIAL.includes(miniLot.type),
			objType: 'lot',
			objId: 'minilot'
		} as LotMatchPartner
	}

	if (!id) {
		console.error('id is required')
		return undefined
	}

	if (!type) {
		console.error('type is required')
		return undefined
	}

	if (type === 'pop') {
		const pop = await getPopInformation(id)
		if (pop) {
			return {
				...pop,
				objId: pop.id,
				objType: 'pop',
				label: pop.label,
				providesMaterial: pop?.materialProvides?.length > 0
			} as unknown as PopMatchPartner
		}
	} else if (type === 'lot') {
		const lot = await getLotInformation(id)
		if (lot) {
			return {
				...lot,
				providesMaterial: LOT_PROVIDES_MATERIAL.includes(lot.type),
				objType: 'lot',
				objId: lot.id
			} as LotMatchPartner
		}
	}
}
